// src/pages/Categories.jsx

import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFilters,
  setFilter,
  resetFilters,
} from "../redux/actions/filterActions";
import { fetchCategoryProducts } from "../redux/actions/categoryActions";
import { useParams, useSearchParams } from "react-router-dom";
import Products from "../components/category/Products";
import { IoMdSearch } from "react-icons/io";
import { BsSliders } from "react-icons/bs";
import PriceFilter from "../components/filter/PriceFilter";
import SizeFilter from "../components/filter/SizeFilter";
import ColorFilter from "../components/filter/ColorFilter";
import TypesFilter from "../components/filter/TypesFilter";
import TagFilter from "../components/filter/TagFilter";

// Skeleton loader for filter sidebar
const FilterSkeleton = () => (
  <div>
    <div className="animate-pulse bg-gray-200 h-8 w-3/4 mb-4 rounded"></div>
    <div className="animate-pulse bg-gray-200 h-6 w-1/2 mb-3 rounded"></div>
    <div className="animate-pulse bg-gray-200 h-6 w-2/3 mb-3 rounded"></div>
    <div className="animate-pulse bg-gray-200 h-6 w-1/3 mb-3 rounded"></div>
    <div className="animate-pulse bg-gray-200 h-8 w-1/2 mb-4 rounded"></div>
  </div>
);

const Categories = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [localSearch, setLocalSearch] = useState("");
  const searchTimeout = useRef();

  // Redux state
  const chosenFilters = useSelector((state) => state.filters);
  const {
    products,
    loading: productsLoading,
    error: productsError,
  } = useSelector((state) => state.categoryProducts);
  const { loading: optionsLoading } = useSelector(
    (state) => state.filterOptions
  );

  const { category } = useParams();

  // Fetch filter options only once on mount
  useEffect(() => {
    dispatch(resetFilters()); // Reset filters on mount
    dispatch(fetchFilters());
  }, [dispatch]);

  // Fetch products when category or filters change
  useEffect(() => {
    if (!category) return;

    const filter = { ...chosenFilters };
    filter.categories = category;

    dispatch(fetchCategoryProducts(filter));
  }, [category, chosenFilters, dispatch]);

  // Debounced search input handler
  const onSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      dispatch(setFilter("searchTerm", value.toLowerCase()));
    }, 400);
  };

  // Reset button handler (preserve category in URL and Redux)
  const onReset = () => {
    dispatch(resetFilters());
    setLocalSearch("");
    if (category) dispatch(setFilter("categories", [category]));
    setSearchParams({ category }, { replace: true });
  };

  return (
    <section className="w-full p-4">
      <div className="flex flex-col lg:flex-row gap-5">
        {/* Filter Sidebar */}
        <div className="w-full lg:w-1/4 p-2">
          <div
            onClick={() => setIsFilterOpen((prev) => !prev)}
            className="flex items-center gap-4 xl:text-xl cursor-pointer"
          >
            <BsSliders />
            <span>Filter</span>
          </div>

          {/* Search Bar */}
          <div className="bg-search-bg rounded-2xl p-2 mt-5 flex items-center">
            <input
              type="text"
              placeholder="Search Product"
              value={localSearch}
              onChange={onSearchChange}
              className="bg-search-bg border-none outline-none w-full"
            />
            <IoMdSearch className="text-xl text-gray-400" />
          </div>

          {/* Filters or Skeleton Loader */}
          {isFilterOpen && (
            <>
              {optionsLoading ? (
                <FilterSkeleton />
              ) : (
                <>
                  <PriceFilter />
                  <SizeFilter />
                  <ColorFilter showRadio={false} />
                  <TypesFilter />
                  <TagFilter />
                  <button
                    onClick={onReset}
                    className="mt-4 w-full py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
                  >
                    Reset Filters
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {/* Product Display Column */}
        <div className="w-full lg:w-3/4 p-2">
          <div className="p-2 my-3 border-b border-medium-theme-color" />
          {productsLoading && <p>Loading...</p>}
          {productsError && <p className="text-red-500">{productsError}</p>}
          {!productsLoading && !productsError && (
            <Products products={products} />
          )}
        </div>
      </div>
    </section>
  );
};

export default Categories;
